// JewelleryWorkOrdersTab.jsx — Phase 2F
// 3-section list (Overdue / Today / Active), separate new order + repair flows,
// 7-stage status for new orders, 4-stage for repairs.

import { useState, useEffect, useCallback, useMemo } from "react";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

// ─── Status config ────────────────────────────────────────────────────────
const NEW_STATUSES    = ["taken","designing","casting","setting","polishing","ready","delivered"];
const REPAIR_STATUSES = ["received","in-progress","ready","delivered"];

const STATUS_CFG = {
  taken:        { label: "Taken",       color: "#3b82f6", bg: "#eff6ff" },
  designing:    { label: "Designing",   color: "#8b5cf6", bg: "#f5f3ff" },
  casting:      { label: "Casting",     color: "#f97316", bg: "#fff7ed" },
  setting:      { label: "Setting",     color: "#d97706", bg: "#fffbeb" },
  polishing:    { label: "Polishing",   color: "#0891b2", bg: "#ecfeff" },
  ready:        { label: "Ready",       color: "#16a34a", bg: "#f0fdf4" },
  delivered:    { label: "Delivered",   color: "#888",    bg: "#f5f5f5" },
  received:     { label: "Received",    color: "#3b82f6", bg: "#eff6ff" },
  "in-progress":{ label: "In Progress", color: "#d97706", bg: "#fffbeb" },
};

const METALS  = ["Gold","Silver","Platinum","Rose Gold"];
const PURITIES = { Gold: ["24K","22K","18K","14K"], Silver: ["999","925"], Platinum: ["950","900"], "Rose Gold": ["18K","14K"] };
const ISSUE_TYPES = ["repair","resize","cleaning","replating","polishing"];
const STONE_TYPES = ["Diamond","Ruby","Emerald","Sapphire","Pearl","Kundan","Polki","Coral","Other"];

function nextStatus(wo) {
  const progression = wo.orderSubtype === "repair" ? REPAIR_STATUSES : NEW_STATUSES;
  const idx = progression.indexOf(wo.status);
  return idx >= 0 && idx < progression.length - 1 ? progression[idx + 1] : null;
}

function formatDate(str) {
  if (!str) return "";
  return new Date(str + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

function isOverdue(wo) {
  if (!wo.deliveryDate || wo.status === "delivered") return false;
  return wo.deliveryDate < todayStr();
}

function isToday(wo) { return wo.deliveryDate === todayStr(); }

function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function inr(n) {
  if (!n && n !== 0) return "";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function JewelleryWorkOrdersTab({ business, catalogue, items: itemsProp }) {
  const [orders, setOrders]         = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [catalogueItems, setCatalogueItems] = useState(itemsProp || []);
  const [loading, setLoading]       = useState(true);
  const [showNew, setShowNew]       = useState(false);      // new order drawer
  const [showRepair, setShowRepair] = useState(false);      // repair drawer
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [viewMeasFor, setViewMeasFor] = useState(null);

  const businessId = business?.businessId;

  const loadOrders = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res  = await fetch(`${API}/workorders?businessId=${businessId}`);
      const data = await res.json();
      const all  = (data.workorders || data.workOrders || [])
        .filter(w => w.orderType === "jewellery" || w.customerId);
      setOrders(all);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [businessId]);

  const loadCustomers = useCallback(async () => {
    if (!businessId) return;
    try {
      const res  = await fetch(`${API}/customers?businessId=${businessId}`);
      const data = await res.json();
      setCustomers(data.customers || []);
    } catch (e) { console.error(e); }
  }, [businessId]);

  const loadItems = useCallback(async () => {
    const catalogueId = catalogue?.catalogueId;
    if (!catalogueId) return;
    try {
      const res  = await fetch(`${API}/items?catalogueId=${catalogueId}`);
      const data = await res.json();
      if (data.items?.length) setCatalogueItems(data.items);
    } catch (e) { console.error(e); }
  }, [catalogue?.catalogueId]);

  useEffect(() => { loadOrders(); loadCustomers(); loadItems(); }, [loadOrders, loadCustomers, loadItems]);

  const advanceStatus = async (wo) => {
    const next = nextStatus(wo);
    if (!next) return;
    setUpdatingId(wo.workOrderId);
    try {
      const res  = await fetch(`${API}/workorders/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workOrderId: wo.workOrderId, targetStatus: next }),
      });
      const data = await res.json();
      if (data.success) {
        setOrders(prev => prev.map(w =>
          w.workOrderId === wo.workOrderId
            ? { ...w, status: data.status, updatedAt: new Date().toISOString() }
            : w
        ));
      }
    } catch { alert("Failed to update status"); }
    finally { setUpdatingId(null); }
  };

  const openWhatsApp = (wo) => {
    const phone   = (wo.customerPhone || "").replace(/\D/g, "");
    const isRepair = wo.orderSubtype === "repair";
    const item    = isRepair ? wo.repairItems?.[0] : wo.designs?.[0];
    const desc    = isRepair ? (item?.description || "your jewellery") : (item?.name || "your order");
    const status  = STATUS_CFG[wo.status]?.label || wo.status;
    const shop    = business?.businessName || "Roshan Jewellers";
    let msg = `Hi ${wo.customerName}, your *${desc}* is now *${status}*.`;
    if (wo.status === "ready") msg += `\nPlease collect from ${shop}.`;
    msg += `\nOrder: ${wo.orderNumber}`;
    const url = phone
      ? `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const { overdue, today, active, delivered } = useMemo(() => {
    const overdue   = orders.filter(isOverdue).sort((a,b) => a.deliveryDate.localeCompare(b.deliveryDate));
    const today     = orders.filter(isToday);
    const active    = orders.filter(w => !isOverdue(w) && !isToday(w) && w.status !== "delivered")
                            .sort((a,b) => (a.deliveryDate||"9999").localeCompare(b.deliveryDate||"9999"));
    const delivered = orders.filter(w => w.status === "delivered");
    return { overdue, today, active, delivered };
  }, [orders]);

  const findCustomer = (id) => customers.find(c => c.customerId === id);
  const cardProps = { advanceStatus, updatingId, openWhatsApp, expandedId, setExpandedId, findCustomer, setViewMeasFor };

  return (
    <div style={{ paddingBottom: 100 }}>
      {/* Header */}
      <div style={st.header}>
        <div>
          <div style={st.title}>Work Orders</div>
          <div style={st.subtitle}>{orders.filter(w => w.status !== "delivered").length} active</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...st.addBtn, background: "#dc2626" }} onClick={() => setShowRepair(true)}>
            🔧 Repair
          </button>
          <button style={st.addBtn} onClick={() => setShowNew(true)}>
            + New
          </button>
        </div>
      </div>

      {loading ? (
        <div style={st.empty}>Loading…</div>
      ) : orders.length === 0 ? (
        <div style={st.empty}>No work orders yet. Tap + New or 🔧 Repair.</div>
      ) : (
        <>
          {overdue.length > 0 && (
            <Section title={`🔴 Overdue (${overdue.length})`} accent="#dc2626" defaultOpen>
              {overdue.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...cardProps} />)}
            </Section>
          )}
          {today.length > 0 && (
            <Section title={`📅 Today's Deliveries (${today.length})`} accent="#d97706" defaultOpen>
              {today.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...cardProps} />)}
            </Section>
          )}
          {active.length > 0 && (
            <Section title={`Active (${active.length})`} accent="#6366f1" defaultOpen>
              {active.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...cardProps} />)}
            </Section>
          )}
          {delivered.length > 0 && (
            <Section title={`Delivered (${delivered.length})`} accent="#888" defaultOpen={false}>
              {delivered.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...cardProps} />)}
            </Section>
          )}
        </>
      )}

      {showNew && (
        <NewOrderDrawer
          business={business}
          customers={customers}
          catalogueItems={catalogueItems}
          onClose={() => setShowNew(false)}
          onCreate={(wo) => { setOrders(p => [wo, ...p]); setShowNew(false); setExpandedId(wo.workOrderId); }}
        />
      )}

      {showRepair && (
        <RepairDrawer
          business={business}
          customers={customers}
          onClose={() => setShowRepair(false)}
          onCreate={(wo) => { setOrders(p => [wo, ...p]); setShowRepair(false); setExpandedId(wo.workOrderId); }}
        />
      )}

      {viewMeasFor && (
        <MeasurementsDrawer customer={viewMeasFor} onClose={() => setViewMeasFor(null)} />
      )}
    </div>
  );
}

// ─── Section ───────────────────────────────────────────────────────────────
function Section({ title, accent, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 4 }}>
      <div onClick={() => setOpen(o => !o)} style={{ ...st.sectionHeader, borderLeft: `3px solid ${accent}` }}>
        <span style={{ ...st.sectionTitle, color: accent }}>{title}</span>
        <span style={{ fontSize: 14, color: "#aaa" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: "0 16px" }}>{children}</div>}
    </div>
  );
}

// ─── Work Order Card ────────────────────────────────────────────────────────
function WorkOrderCard({ wo, advanceStatus, updatingId, openWhatsApp, expandedId, setExpandedId, findCustomer, setViewMeasFor }) {
  const cfg       = STATUS_CFG[wo.status] || STATUS_CFG.taken;
  const next      = nextStatus(wo);
  const isExpanded = expandedId === wo.workOrderId;
  const isUpdating = updatingId === wo.workOrderId;
  const overdue   = isOverdue(wo);
  const todayDel  = isToday(wo);
  const isRepair  = wo.orderSubtype === "repair";
  const customer  = findCustomer(wo.customerId);

  // First design or repair item for summary
  const item = isRepair ? wo.repairItems?.[0] : wo.designs?.[0];

  const progression = isRepair ? REPAIR_STATUSES : NEW_STATUSES;
  const statusIdx   = progression.indexOf(wo.status);

  const daysUntil = wo.deliveryDate
    ? Math.ceil((new Date(wo.deliveryDate + "T00:00:00") - new Date()) / 86400000)
    : null;

  return (
    <div style={{
      ...st.card,
      borderLeft: `4px solid ${overdue ? "#dc2626" : todayDel ? "#d97706" : cfg.color}`,
    }}>
      <div style={st.cardHeader} onClick={() => setExpandedId(isExpanded ? null : wo.workOrderId)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={st.orderRef}>{wo.orderNumber}</span>
            {isRepair && <span style={st.repairBadge}>REPAIR</span>}
            {wo.deliveryDate && (
              <span style={{ fontSize: 11, fontWeight: 600, color: overdue ? "#dc2626" : todayDel ? "#d97706" : "#888" }}>
                📅 {formatDate(wo.deliveryDate)}
                {overdue && ` · ${Math.abs(daysUntil)}d overdue`}
                {todayDel && " · Today"}
                {!overdue && !todayDel && daysUntil !== null && daysUntil <= 3 && ` · ${daysUntil}d left`}
              </span>
            )}
          </div>

          <div style={st.customerName}>{wo.customerName}</div>

          {item && (
            <div style={st.designSummary}>
              {isRepair
                ? item.description
                : `${item.name}${item.variantSelected ? ` · ${item.variantSelected}` : ""}${item.metal ? ` · ${item.metal} ${item.purity || ""}` : ""}`
              }
            </div>
          )}

          {/* Progress dots */}
          <div style={st.progressRow}>
            {progression.map((s, i) => {
              const isDone    = i < statusIdx;
              const isCurrent = i === statusIdx;
              return (
                <div key={s} style={{
                  width: isCurrent ? 10 : 8,
                  height: isCurrent ? 10 : 8,
                  borderRadius: "50%",
                  background: isDone || isCurrent ? cfg.color : "#e5e7eb",
                  border: isCurrent ? `2px solid ${cfg.color}` : "none",
                  boxShadow: isCurrent ? `0 0 0 2px ${cfg.bg}` : "none",
                  flexShrink: 0,
                }} />
              );
            })}
            <span style={{ ...st.statusLabel, color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
          </div>
        </div>
        <div style={{ color: "#bbb", fontSize: 18, flexShrink: 0 }}>{isExpanded ? "▴" : "▾"}</div>
      </div>

      {/* Expanded */}
      {isExpanded && (
        <div style={st.detail}>
          {/* New order designs */}
          {!isRepair && wo.designs?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={st.detailLabel}>Designs</div>
              {wo.designs.map((d, i) => (
                <div key={i} style={st.designRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</div>
                    {d.variantSelected && <div style={{ fontSize: 12, color: "#666" }}>Style: {d.variantSelected}</div>}
                    <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                      {[d.metal, d.purity, d.weightGrams && `${d.weightGrams}g`].filter(Boolean).join(" · ")}
                    </div>
                    {d.stones?.length > 0 && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        Stones: {d.stones.map(s => `${s.type}${s.carats ? ` ${s.carats}ct` : ""}${s.pieces ? ` ×${s.pieces}` : ""}`).join(", ")}
                      </div>
                    )}
                    {d.makingCharges > 0 && (
                      <div style={{ fontSize: 11, color: "#b8722a", fontWeight: 600, marginTop: 2 }}>
                        Making: {inr(d.makingCharges)}{d.makingChargeType === "per-gram" ? "/g" : " flat"}
                      </div>
                    )}
                    {d.notes && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Note: {d.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Repair items */}
          {isRepair && wo.repairItems?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={st.detailLabel}>Repair Details</div>
              {wo.repairItems.map((r, i) => (
                <div key={i} style={st.designRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.description}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {[r.metal, r.purity, r.issueType].filter(Boolean).join(" · ")}
                    </div>
                    {r.estimatedCost > 0 && (
                      <div style={{ fontSize: 11, color: "#b8722a", fontWeight: 600, marginTop: 2 }}>
                        Estimated: {inr(r.estimatedCost)}
                      </div>
                    )}
                    {r.notes && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Note: {r.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Status history */}
          <div style={{ marginBottom: 12 }}>
            <div style={st.detailLabel}>Progress</div>
            <div style={st.historyRow}>
              {(wo.statusHistory || []).map((h, i) => (
                <div key={i} style={st.historyItem}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_CFG[h.status]?.color || "#888", marginTop: 3, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#333", textTransform: "capitalize" }}>{h.status}</div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>{timeAgo(h.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {wo.notes && <div style={st.notesBox}>📝 {wo.notes}</div>}

          {customer && (
            <button onClick={() => setViewMeasFor(customer)} style={st.measLink}>
              💍 View {wo.customerName.split(" ")[0]}'s sizing
            </button>
          )}

          <div style={st.actionRow}>
            <button onClick={() => openWhatsApp(wo)} style={st.whatsappBtn}>💬 WhatsApp</button>
            {next && (
              <button
                onClick={() => advanceStatus(wo)}
                disabled={isUpdating}
                style={{ ...st.advanceBtn, background: STATUS_CFG[next]?.color || "#111", opacity: isUpdating ? 0.5 : 1 }}
              >
                {isUpdating ? "Updating…" : `→ ${STATUS_CFG[next]?.label}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── New Order Drawer ─────────────────────────────────────────────────────
function NewOrderDrawer({ business, customers, catalogueItems, onClose, onCreate }) {
  const [step, setStep]           = useState(1);
  const [selCustomer, setSelCustomer] = useState(null);
  const [selDesigns, setSelDesigns]   = useState([]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [notes, setNotes]         = useState("");
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState("");
  const [showInline, setShowInline] = useState(false);
  const [inlineName, setInlineName] = useState("");
  const [inlinePhone, setInlinePhone] = useState("");
  const [inlineSaving, setInlineSaving] = useState(false);
  const [allCustomers, setAllCustomers] = useState(customers);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCustomers;
    return allCustomers.filter(c => (c.name||"").toLowerCase().includes(q) || (c.phone||"").includes(q));
  }, [allCustomers, search]);

  const handleInlineCreate = async () => {
    if (!inlineName.trim() || !inlinePhone.trim()) return;
    setInlineSaving(true);
    try {
      const res = await fetch(`${API}/customers`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId: business.businessId, name: inlineName.trim(), phone: inlinePhone.trim(), tags: [], measurementUnit: "mm", measurements: {}, notes: "" }),
      });
      const data = await res.json();
      const nc = data.customer || { customerId: data.customerId, name: inlineName.trim(), phone: inlinePhone.trim(), tags: [], measurements: {}, measurementUnit: "mm" };
      setAllCustomers(p => [nc, ...p]);
      setSelCustomer(nc);
      setShowInline(false); setInlineName(""); setInlinePhone("");
    } catch (e) { alert("Could not create: " + e.message); }
    finally { setInlineSaving(false); }
  };

  const toggleDesign = (item) => {
    setSelDesigns(prev => {
      const exists = prev.find(d => d.itemId === item.itemId);
      if (exists) return prev.filter(d => d.itemId !== item.itemId);
      const variantNames = (item.variants||[]).map(v => typeof v === "string" ? v : v?.name||"").filter(Boolean);
      return [...prev, {
        itemId: item.itemId, name: item.name, category: item.category||"",
        variantSelected: variantNames[0]||"",
        metal: item.specs?.metal || "Gold",
        purity: item.specs?.purity || "22K",
        weightGrams: "", stones: [], makingCharges: "", makingChargeType: "flat", notes: "",
      }];
    });
  };

  const updateDesign = (itemId, key, val) => {
    setSelDesigns(p => p.map(d => d.itemId === itemId ? { ...d, [key]: val } : d));
  };

  const handleCreate = async () => {
    if (!selCustomer || selDesigns.length === 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/workorders`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId: business.businessId, orderType: "jewellery", orderSubtype: "new",
          customerId: selCustomer.customerId, customerName: selCustomer.name, customerPhone: selCustomer.phone||"",
          designs: selDesigns.map(d => ({
            ...d,
            weightGrams:   d.weightGrams ? Number(d.weightGrams) : null,
            makingCharges: d.makingCharges ? Number(d.makingCharges) : 0,
          })),
          deliveryDate: deliveryDate||null, notes: notes.trim(),
        }),
      });
      const data = await res.json();
      if (data.workOrder || data.workorder) onCreate(data.workOrder || data.workorder);
      else alert("Failed to create order");
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={st.drawerBackdrop} onClick={onClose}>
      <div style={st.drawer} onClick={e => e.stopPropagation()}>
        <div style={st.drawerHeader}>
          <div>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              New Jewellery Order · Step {step} of 3
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111", marginTop: 2 }}>
              {step === 1 ? "Pick Customer" : step === 2 ? "Pick Designs" : "Delivery Details"}
            </div>
          </div>
          <button onClick={onClose} style={st.closeBtn}>✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, padding: "0 16px 12px" }}>
          {[1,2,3].map(n => (
            <div key={n} style={{ flex: 1, height: 3, borderRadius: 2, background: n <= step ? "#b8860b" : "#e5e7eb" }} />
          ))}
        </div>

        {/* Step 1: Customer */}
        {step === 1 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
            {showInline ? (
              <div style={{ background: "#fffbeb", borderRadius: 12, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#b8860b", marginBottom: 10 }}>New Customer</div>
                <input autoFocus value={inlineName} onChange={e => setInlineName(e.target.value)} placeholder="Customer name" style={{ ...st.input, marginBottom: 8 }} />
                <input value={inlinePhone} onChange={e => setInlinePhone(e.target.value)} placeholder="Phone" inputMode="tel" style={{ ...st.input, marginBottom: 10 }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setShowInline(false); setInlineName(""); setInlinePhone(""); }} style={st.backBtn}>Cancel</button>
                  <button onClick={handleInlineCreate} disabled={inlineSaving || !inlineName.trim() || !inlinePhone.trim()} style={{ ...st.nextBtn, opacity: (inlineSaving || !inlineName.trim() || !inlinePhone.trim()) ? 0.4 : 1 }}>
                    {inlineSaving ? "Saving…" : "Save & Select"}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowInline(true)} style={st.newCustomerBtn}>＋ New Customer</button>
            )}
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search existing customers…" style={{ ...st.input, marginBottom: 12 }} />
            {filtered.map(c => {
              const isSelected = selCustomer?.customerId === c.customerId;
              const hasJwl = c.measurements?.ringSize != null;
              return (
                <div key={c.customerId} onClick={() => setSelCustomer(c)} style={{ ...st.pickerRow, ...(isSelected ? st.pickerRowSelected : {}) }}>
                  <div style={st.customerAvatar}>{(c.name||"?").charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{c.phone}</div>
                    <div style={{ fontSize: 11, color: hasJwl ? "#16a34a" : "#aaa", marginTop: 2 }}>
                      {hasJwl ? `💍 Ring size: ${c.measurements.ringSize}` : "No sizing on file"}
                    </div>
                  </div>
                  {isSelected && <div style={{ color: "#b8860b", fontSize: 20 }}>✓</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Step 2: Designs */}
        {step === 2 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
            {catalogueItems.length === 0 && <div style={st.empty}>No designs in catalogue. Add them in the Designs tab.</div>}
            {catalogueItems.map(item => {
              const isSelected = !!selDesigns.find(d => d.itemId === item.itemId);
              const selDesign  = selDesigns.find(d => d.itemId === item.itemId);
              const variantNames = (item.variants||[]).map(v => typeof v === "string" ? v : v?.name||"").filter(Boolean);
              const thumb = Array.isArray(item.photos) ? item.photos[0] : null;
              return (
                <div key={item.itemId} style={{ marginBottom: 10 }}>
                  <div onClick={() => toggleDesign(item)} style={{ ...st.pickerRow, ...(isSelected ? st.pickerRowSelected : {}) }}>
                    {thumb
                      ? <img src={thumb} alt={item.name||""} style={st.designThumb} />
                      : <div style={{ ...st.designThumb, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>💍</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{item.name||"Unnamed"}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{item.category||""}</div>
                      {item.specs && <div style={{ fontSize: 11, color: "#b8860b" }}>{[item.specs.metal, item.specs.purity, item.specs.weight].filter(Boolean).join(" · ")}</div>}
                    </div>
                    {isSelected && <div style={{ color: "#b8860b", fontSize: 20, flexShrink: 0 }}>✓</div>}
                  </div>

                  {isSelected && selDesign && (
                    <div style={{ background: "#fffbeb", borderRadius: 10, padding: "10px 12px", marginTop: 4 }} onClick={e => e.stopPropagation()}>
                      {variantNames.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={st.miniLabel}>Style / Variant</div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {variantNames.map(v => (
                              <button key={v} onClick={() => updateDesign(item.itemId, "variantSelected", v)}
                                style={{ padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, border: "1.5px solid", cursor: "pointer",
                                  borderColor: selDesign.variantSelected === v ? "#b8860b" : "#ddd",
                                  background:  selDesign.variantSelected === v ? "#b8860b" : "#fff",
                                  color:       selDesign.variantSelected === v ? "#fff"    : "#666",
                                }}>{v}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <div>
                          <div style={st.miniLabel}>Metal</div>
                          <select value={selDesign.metal} onChange={e => {
                            updateDesign(item.itemId, "metal", e.target.value);
                            updateDesign(item.itemId, "purity", PURITIES[e.target.value]?.[0]||"");
                          }} style={st.select}>
                            {METALS.map(m => <option key={m}>{m}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={st.miniLabel}>Purity</div>
                          <select value={selDesign.purity} onChange={e => updateDesign(item.itemId, "purity", e.target.value)} style={st.select}>
                            {(PURITIES[selDesign.metal]||["22K","18K"]).map(p => <option key={p}>{p}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={st.miniLabel}>Est. Weight (g)</div>
                          <input type="number" value={selDesign.weightGrams} onChange={e => updateDesign(item.itemId, "weightGrams", e.target.value)} placeholder="e.g. 12" style={st.input} />
                        </div>
                        <div>
                          <div style={st.miniLabel}>Making Charges (₹)</div>
                          <input type="number" value={selDesign.makingCharges} onChange={e => updateDesign(item.itemId, "makingCharges", e.target.value)} placeholder="e.g. 3500" style={st.input} />
                        </div>
                      </div>
                      <div>
                        <div style={st.miniLabel}>Design Notes</div>
                        <input value={selDesign.notes||""} onChange={e => updateDesign(item.itemId, "notes", e.target.value)} placeholder="e.g. Confirm stone colour before setting…" style={st.input} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Step 3: Delivery details */}
        {step === 3 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
            <div style={st.summaryBox}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111", marginBottom: 4 }}>{selCustomer?.name}</div>
              <div style={{ fontSize: 13, color: "#666" }}>
                {selDesigns.map(d => `${d.name}${d.variantSelected ? ` (${d.variantSelected})` : ""} · ${d.metal} ${d.purity}`).join(", ")}
              </div>
              {selCustomer?.measurements?.ringSize && (
                <div style={{ fontSize: 11, color: "#b8860b", marginTop: 4 }}>💍 Ring size: {selCustomer.measurements.ringSize}</div>
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={st.detailLabel}>Delivery Date</div>
              <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} min={todayStr()} style={st.input} />
            </div>
            <div>
              <div style={st.detailLabel}>Order Notes</div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special instructions…" rows={3} style={{ ...st.input, resize: "vertical" }} />
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={st.drawerFooter}>
          {step > 1 && <button onClick={() => setStep(s => s-1)} style={st.backBtn}>← Back</button>}
          {step < 3 && (
            <button onClick={() => setStep(s => s+1)}
              disabled={step === 1 ? !selCustomer : selDesigns.length === 0}
              style={{ ...st.nextBtn, opacity: (step === 1 ? !selCustomer : selDesigns.length === 0) ? 0.4 : 1 }}>
              Next →
            </button>
          )}
          {step === 3 && (
            <button onClick={handleCreate} disabled={saving} style={{ ...st.nextBtn, opacity: saving ? 0.5 : 1 }}>
              {saving ? "Creating…" : "Create Order"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Repair Drawer ─────────────────────────────────────────────────────────
function RepairDrawer({ business, customers, onClose, onCreate }) {
  const [selCustomer, setSelCustomer] = useState(null);
  const [search, setSearch]           = useState("");
  const [showInline, setShowInline]   = useState(false);
  const [inlineName, setInlineName]   = useState("");
  const [inlinePhone, setInlinePhone] = useState("");
  const [inlineSaving, setInlineSaving] = useState(false);
  const [allCustomers, setAllCustomers] = useState(customers);
  const [description, setDescription]  = useState("");
  const [metal, setMetal]              = useState("Gold");
  const [purity, setPurity]            = useState("22K");
  const [issueType, setIssueType]      = useState("repair");
  const [estimatedCost, setEstimatedCost] = useState("");
  const [deliveryDate, setDeliveryDate]   = useState("");
  const [notes, setNotes]               = useState("");
  const [saving, setSaving]             = useState(false);
  const [step, setStep]                 = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCustomers;
    return allCustomers.filter(c => (c.name||"").toLowerCase().includes(q) || (c.phone||"").includes(q));
  }, [allCustomers, search]);

  const handleInlineCreate = async () => {
    if (!inlineName.trim() || !inlinePhone.trim()) return;
    setInlineSaving(true);
    try {
      const res = await fetch(`${API}/customers`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId: business.businessId, name: inlineName.trim(), phone: inlinePhone.trim(), tags: [], measurementUnit: "mm", measurements: {}, notes: "" }),
      });
      const data = await res.json();
      const nc = data.customer || { customerId: data.customerId, name: inlineName.trim(), phone: inlinePhone.trim(), tags: [], measurements: {}, measurementUnit: "mm" };
      setAllCustomers(p => [nc, ...p]);
      setSelCustomer(nc);
      setShowInline(false); setInlineName(""); setInlinePhone("");
    } catch (e) { alert("Could not create: " + e.message); }
    finally { setInlineSaving(false); }
  };

  const handleCreate = async () => {
    if (!selCustomer || !description.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/workorders`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId: business.businessId, orderType: "jewellery", orderSubtype: "repair",
          customerId: selCustomer.customerId, customerName: selCustomer.name, customerPhone: selCustomer.phone||"",
          repairItems: [{ description: description.trim(), metal, purity, issueType, estimatedCost: Number(estimatedCost)||0, notes: notes.trim() }],
          deliveryDate: deliveryDate||null, notes: notes.trim(),
        }),
      });
      const data = await res.json();
      if (data.workOrder || data.workorder) onCreate(data.workOrder || data.workorder);
      else alert("Failed to create repair order");
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={st.drawerBackdrop} onClick={onClose}>
      <div style={st.drawer} onClick={e => e.stopPropagation()}>
        <div style={st.drawerHeader}>
          <div>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Repair Order · Step {step} of 2
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#dc2626", marginTop: 2 }}>
              {step === 1 ? "Pick Customer" : "Repair Details"}
            </div>
          </div>
          <button onClick={onClose} style={st.closeBtn}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 4, padding: "0 16px 12px" }}>
          {[1,2].map(n => (
            <div key={n} style={{ flex: 1, height: 3, borderRadius: 2, background: n <= step ? "#dc2626" : "#e5e7eb" }} />
          ))}
        </div>

        {step === 1 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
            {showInline ? (
              <div style={{ background: "#fff0f0", borderRadius: 12, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 10 }}>New Customer</div>
                <input autoFocus value={inlineName} onChange={e => setInlineName(e.target.value)} placeholder="Customer name" style={{ ...st.input, marginBottom: 8 }} />
                <input value={inlinePhone} onChange={e => setInlinePhone(e.target.value)} placeholder="Phone" inputMode="tel" style={{ ...st.input, marginBottom: 10 }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setShowInline(false); setInlineName(""); setInlinePhone(""); }} style={st.backBtn}>Cancel</button>
                  <button onClick={handleInlineCreate} disabled={inlineSaving||!inlineName.trim()||!inlinePhone.trim()}
                    style={{ ...st.nextBtn, background: "#dc2626", opacity: (inlineSaving||!inlineName.trim()||!inlinePhone.trim()) ? 0.4 : 1 }}>
                    {inlineSaving ? "Saving…" : "Save & Select"}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowInline(true)} style={{ ...st.newCustomerBtn, borderColor: "#fecaca", background: "#fff0f0", color: "#dc2626" }}>＋ New Customer</button>
            )}
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search existing customers…" style={{ ...st.input, marginBottom: 12 }} />
            {filtered.map(c => {
              const isSelected = selCustomer?.customerId === c.customerId;
              return (
                <div key={c.customerId} onClick={() => setSelCustomer(c)} style={{ ...st.pickerRow, ...(isSelected ? { ...st.pickerRowSelected, border: "2px solid #dc2626", background: "#fff0f0" } : {}) }}>
                  <div style={st.customerAvatar}>{(c.name||"?").charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{c.phone}</div>
                  </div>
                  {isSelected && <div style={{ color: "#dc2626", fontSize: 20 }}>✓</div>}
                </div>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 80px" }}>
            <div style={{ marginBottom: 12 }}>
              <div style={st.detailLabel}>Item Description *</div>
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. 22K gold chain – 3 links broken near clasp" style={st.input} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <div style={st.detailLabel}>Metal</div>
                <select value={metal} onChange={e => { setMetal(e.target.value); setPurity(PURITIES[e.target.value]?.[0]||""); }} style={st.select}>
                  {METALS.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <div style={st.detailLabel}>Purity</div>
                <select value={purity} onChange={e => setPurity(e.target.value)} style={st.select}>
                  {(PURITIES[metal]||["22K","18K"]).map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <div style={st.detailLabel}>Repair Type</div>
                <select value={issueType} onChange={e => setIssueType(e.target.value)} style={st.select}>
                  {ISSUE_TYPES.map(t => <option key={t} value={t} style={{ textTransform: "capitalize" }}>{t}</option>)}
                </select>
              </div>
              <div>
                <div style={st.detailLabel}>Estimated Cost (₹)</div>
                <input type="number" value={estimatedCost} onChange={e => setEstimatedCost(e.target.value)} placeholder="e.g. 500" style={st.input} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={st.detailLabel}>Delivery Date</div>
              <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} min={todayStr()} style={st.input} />
            </div>
            <div>
              <div style={st.detailLabel}>Notes</div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Handle carefully — vintage piece" rows={2} style={{ ...st.input, resize: "vertical" }} />
            </div>
          </div>
        )}

        <div style={st.drawerFooter}>
          {step > 1 && <button onClick={() => setStep(1)} style={st.backBtn}>← Back</button>}
          {step === 1 && (
            <button onClick={() => setStep(2)} disabled={!selCustomer}
              style={{ ...st.nextBtn, background: "#dc2626", opacity: !selCustomer ? 0.4 : 1 }}>
              Next →
            </button>
          )}
          {step === 2 && (
            <button onClick={handleCreate} disabled={saving || !description.trim()}
              style={{ ...st.nextBtn, background: "#dc2626", opacity: (saving||!description.trim()) ? 0.5 : 1 }}>
              {saving ? "Creating…" : "Create Repair Order"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Customer Measurements Drawer (jewellery sizing) ───────────────────────
function MeasurementsDrawer({ customer, onClose }) {
  const m    = customer.measurements || {};
  const JWL_FIELDS = [
    { key: "ringSize",    label: "Ring Size",    unit: "" },
    { key: "wristCircum", label: "Wrist",        unit: "mm" },
    { key: "neckCircum",  label: "Neck",         unit: "mm" },
    { key: "fingerLength",label: "Finger Length", unit: "mm" },
  ];

  return (
    <div style={{ ...st.drawerBackdrop, zIndex: 1100 }} onClick={onClose}>
      <div style={{ ...st.drawer, height: "50vh" }} onClick={e => e.stopPropagation()}>
        <div style={st.drawerHeader}>
          <div>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Jewellery Sizing</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111", marginTop: 2 }}>{customer.name}</div>
          </div>
          <button onClick={onClose} style={st.closeBtn}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
          {m.notes && <div style={st.notesBox}>✂️ {m.notes}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {JWL_FIELDS.map(f => {
              const val    = m[f.key];
              const filled = val != null && val !== "";
              return (
                <div key={f.key} style={{ background: filled ? "#fffbeb" : "#fafafa", border: `1.5px solid ${filled ? "#fde68a" : "#efefed"}`, borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 4 }}>{f.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: filled ? "#92400e" : "#ccc", fontVariantNumeric: "tabular-nums" }}>
                    {filled ? val : "—"}
                    {filled && f.unit && <span style={{ fontSize: 11, color: "#888", marginLeft: 2 }}>{f.unit}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {customer.notes && <div style={{ ...st.notesBox, marginTop: 12 }}>📝 {customer.notes}</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────
const st = {
  header:       { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 16px 12px" },
  title:        { fontSize: 18, fontWeight: 700, color: "#111" },
  subtitle:     { fontSize: 12, color: "#888", marginTop: 2 },
  addBtn:       { background: "#111", color: "#fff", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  empty:        { padding: "40px 16px", textAlign: "center", color: "#aaa", fontSize: 14 },
  sectionHeader:{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", cursor: "pointer", marginBottom: 8 },
  sectionTitle: { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 },
  card:         { background: "#fff", borderRadius: 14, marginBottom: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" },
  cardHeader:   { display: "flex", alignItems: "flex-start", padding: "12px 14px", cursor: "pointer", gap: 8 },
  orderRef:     { fontSize: 12, fontWeight: 800, fontFamily: "monospace", color: "#b8860b", letterSpacing: 0.5 },
  repairBadge:  { fontSize: 9, fontWeight: 800, background: "#fee2e2", color: "#dc2626", padding: "2px 6px", borderRadius: 6, letterSpacing: 0.5 },
  customerName: { fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 2 },
  designSummary:{ fontSize: 12, color: "#666", marginBottom: 6 },
  progressRow:  { display: "flex", alignItems: "center", gap: 5, marginTop: 4, flexWrap: "wrap" },
  statusLabel:  { fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 8, textTransform: "uppercase", letterSpacing: 0.3, marginLeft: 4 },
  detail:       { borderTop: "1px solid #f5f5f5", padding: "12px 14px 14px" },
  detailLabel:  { fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  miniLabel:    { fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 },
  designRow:    { display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #f5f5f5" },
  historyRow:   { display: "flex", gap: 12, flexWrap: "wrap" },
  historyItem:  { display: "flex", gap: 6, alignItems: "flex-start" },
  notesBox:     { fontSize: 13, color: "#666", background: "#f9f9f9", borderRadius: 8, padding: "8px 12px", marginBottom: 10 },
  measLink:     { display: "block", width: "100%", padding: "9px 12px", marginBottom: 10, borderRadius: 10, border: "1.5px solid #fde68a", background: "#fffbeb", color: "#92400e", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left" },
  actionRow:    { display: "flex", gap: 8, marginTop: 4 },
  whatsappBtn:  { padding: "10px 14px", borderRadius: 10, border: "none", background: "#25D366", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  advanceBtn:   { flex: 1, padding: "10px 14px", borderRadius: 10, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  drawerBackdrop:{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  drawer:       { background: "#fff", width: "100%", maxWidth: 520, height: "92vh", borderTopLeftRadius: 20, borderTopRightRadius: 20, display: "flex", flexDirection: "column", overflow: "hidden" },
  drawerHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "16px 16px 8px", borderBottom: "1px solid #f0f0f0" },
  drawerFooter: { display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", flexShrink: 0 },
  closeBtn:     { width: 32, height: 32, borderRadius: 8, border: "none", background: "#f5f5f3", fontSize: 14, color: "#666", cursor: "pointer" },
  backBtn:      { padding: "11px 16px", border: "1.5px solid #efefed", background: "#fff", borderRadius: 11, fontSize: 13, fontWeight: 600, color: "#666", cursor: "pointer" },
  nextBtn:      { flex: 1, padding: "11px 16px", border: "none", background: "#b8860b", borderRadius: 11, fontSize: 14, fontWeight: 700, color: "#fff", cursor: "pointer" },
  newCustomerBtn:{ width: "100%", padding: "10px 14px", marginBottom: 12, borderRadius: 10, border: "1.5px dashed #fde68a", background: "#fffbeb", color: "#92400e", fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "left" },
  pickerRow:    { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #efefed", background: "#fff", marginBottom: 8, cursor: "pointer" },
  pickerRowSelected:{ border: "2px solid #b8860b", background: "#fffbeb" },
  customerAvatar:{ width: 40, height: 40, borderRadius: "50%", background: "#fef3c7", color: "#92400e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, flexShrink: 0 },
  designThumb:  { width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 },
  summaryBox:   { background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 12, padding: "12px 14px", marginBottom: 16 },
  input:        { width: "100%", padding: "10px 12px", border: "1.5px solid #efefed", borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fafaf8", fontFamily: "inherit" },
  select:       { width: "100%", padding: "10px 12px", border: "1.5px solid #efefed", borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fafaf8", fontFamily: "inherit", cursor: "pointer" },
};
